import { apiFetchClient } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import type { SellerImpersonationHandoffResponse } from "@/lib/api/types";
import { resolvePublicSellerRedirectPath } from "@/lib/auth/public-seller-auth";
import {
  isSupportedLocale,
  type PortalLocale,
} from "@/lib/i18n/portal/index";

const IMPERSONATION_SCHEME = "trioty-seller:";
const IMPERSONATION_HOST = "impersonate";
const HANDOFF_CODE_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export type SellerImpersonationHandoff = {
  code: string;
  locale: PortalLocale;
};

export function parseSellerImpersonationHandoffUrl(
  value: string,
): SellerImpersonationHandoff | null {
  try {
    const url = new URL(value);
    if (
      url.protocol !== IMPERSONATION_SCHEME ||
      url.hostname !== IMPERSONATION_HOST ||
      (url.pathname !== "" && url.pathname !== "/")
    ) {
      return null;
    }

    const code = url.searchParams.get("code") ?? "";
    if (!HANDOFF_CODE_PATTERN.test(code)) {
      return null;
    }

    const requestedLocale = url.searchParams.get("locale") ?? "";
    if (!isSupportedLocale(requestedLocale)) {
      return null;
    }

    return {
      code,
      locale: requestedLocale,
    };
  } catch {
    return null;
  }
}

export async function redeemSellerImpersonationHandoff(
  handoff: SellerImpersonationHandoff,
): Promise<string> {
  const response = await apiFetchClient(
    endpoints.public.sellerAuth.impersonationHandoff,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: handoff.code }),
    },
  );

  if (!response.ok) {
    throw new Error("Seller impersonation handoff failed");
  }

  const payload =
    (await response.json()) as SellerImpersonationHandoffResponse;
  return resolvePublicSellerRedirectPath(handoff.locale, payload.redirectPath);
}

type SellerImpersonationHandoffHandlerOptions = {
  redeem: (handoff: SellerImpersonationHandoff) => Promise<string>;
  navigate: (path: string) => void;
};

export function createSellerImpersonationHandoffHandler({
  redeem,
  navigate,
}: SellerImpersonationHandoffHandlerOptions): (value: string) => Promise<void> {
  const claimedCodes = new Set<string>();

  return async (value: string) => {
    const handoff = parseSellerImpersonationHandoffUrl(value);
    if (!handoff || claimedCodes.has(handoff.code)) {
      return;
    }

    claimedCodes.add(handoff.code);
    try {
      navigate(await redeem(handoff));
    } catch (failure) {
      if (failure instanceof DOMException && failure.name === "AbortError") return;
      // The browser tab remains an authenticated fallback. Keep the native app
      // usable without exposing whether a code was invalid, expired, or replayed.
      navigate(`/${handoff.locale}/login`);
    }
  };
}
